import {combine, guard, merge, sample} from 'effector';
import i18next from 'i18next';
import {toast} from 'react-toastify';

import {RestError, showRestError} from 'core/errors';

import {
    organizationList$,
    organizationData$,
    fetchDataFx,
    filters$,
    filtersChanged,
    Gate,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    organizationBalanceChanged,
    fetchOrganizationByTinFx,
    OrganizationGate,
    blockOrganizationFx,
    unblockOrganizationFx,
} from './index';

const fetchOrganiztionDataGuard = guard({
    clock: OrganizationGate.state,
    filter: (params: {tin: string | null}) => !!params.tin && params.tin !== null,
});

sample({
    clock: fetchOrganiztionDataGuard,
    source: OrganizationGate.state,
    fn: (params: {tin: string | null}) => params.tin as string,
    target: fetchOrganizationByTinFx,
});

organizationData$
    .on(fetchOrganizationByTinFx.doneData, (_, data) => data)
    .on(blockOrganizationFx.done, (state) => (state ? {...state, isBlocked: true} : state))
    .on(unblockOrganizationFx.done, (state) => (state ? {...state, isBlocked: false} : state))
    .reset(fetchOrganizationByTinFx)
    .reset(OrganizationGate.close);

organizationList$
    .on(fetchDataFx.doneData, (_, data) => data)
    .on(organizationBalanceChanged, (state, {currentBalance, legalIdentityId}) => {
        if (!state) return state;
        return {
            ...state,
            data: state.data.map((org) =>
                org.id === legalIdentityId ? {...org, balance: currentBalance} : org,
            ),
        };
    })
    .reset(Gate.close);
filters$.reset(Gate.close);
pageNumber$.reset(Gate.close);
pageSize$.reset(Gate.close);

sample({
    clock: merge([Gate.open, pageNumberChanged, pageSizeChanged, filtersChanged]),
    source: combine({
        filters: filters$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
    }),
    fn: ({filters, pageNumber, pageSize}) => {
        const taxIdNumber = filters.find(({id}) => id === 'taxIdNumber')?.value;

        return {
            paging: {
                pageNumber,
                pageSize,
            },
            filters: [],
            sortings: [],
            taxIdNumber: typeof taxIdNumber === 'string' ? taxIdNumber.trim() : undefined,
        };
    },
    target: fetchDataFx,
});

fetchDataFx.fail.watch(({error}) => showRestError(error as RestError));
fetchOrganizationByTinFx.fail.watch(({error}) => showRestError(error as RestError));
blockOrganizationFx.fail.watch(({error}) => showRestError(error as RestError));
unblockOrganizationFx.fail.watch(({error}) => showRestError(error as RestError));

blockOrganizationFx.doneData.watch(() =>
    toast.success(i18next.t('pages.profile.organizations.confirmBlock.successBlock')),
);
unblockOrganizationFx.doneData.watch(() =>
    toast.success(i18next.t('pages.profile.organizations.confirmBlock.successUnblock')),
);
